import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTouchOptimization } from '../hooks/useTouchOptimization';
import { useTouchHandler } from '../hooks/useTouchHandler';
import { Portal } from './Portal';

interface MinimumOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  minimumAmount: number;
  currentAmount: number;
  currency?: string;
}

export const MinimumOrderModal: React.FC<MinimumOrderModalProps> = ({
  isOpen,
  onClose,
  minimumAmount,
  currentAmount,
  currency = '€',
}) => {
  const { t } = useTranslation();
  const difference = minimumAmount - currentAmount;
  const { getTouchClasses } = useTouchOptimization();

  const closeHandler = useTouchHandler({
    onPress: onClose,
    debounceMs: 300,
  });

  const continueHandler = useTouchHandler({
    onPress: onClose,
    debounceMs: 300,
  });

  return (
    <Portal>
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-orange-600 to-red-600 pt-8 pb-12 px-6 text-white">
              <button
                {...closeHandler.touchHandlers}
                className={getTouchClasses("absolute top-4 right-4 text-white/80 hover:text-white transition-all duration-100 active:scale-90 p-2")}
                aria-label={t('Fermer')}
                style={{ touchAction: 'manipulation' }}
              >
                <X size={24} />
              </button>

              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                  <AlertCircle className="text-orange-600" size={32} strokeWidth={2.5} />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-center">{t('Minimum de commande')}</h2>
              <p className="text-orange-100 mt-2 text-sm text-center">
                {t('Le montant minimum de commande n\'est pas atteint')}
              </p>

              {/* Decorative elements */}
              <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Montant actuel */}
              <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="text-white" size={20} />
                  </div>
                  <span className="text-gray-700 font-semibold">
                    {t('Montant actuel')}
                  </span>
                </div>
                <span className="text-xl font-bold text-gray-800">
                  {currentAmount.toFixed(2)} {currency}
                </span>
              </div>

              {/* Minimum requis */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 flex justify-between items-center border-2 border-orange-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                    <AlertCircle className="text-white" size={20} />
                  </div>
                  <span className="text-gray-700 font-semibold">
                    {t('Minimum requis')}
                  </span>
                </div>
                <span className="text-xl font-bold text-orange-600">
                  {minimumAmount.toFixed(2)} {currency}
                </span>
              </div>

              {/* Montant manquant */}
              <div className="bg-gradient-to-br from-orange-500 via-red-500 to-red-600 rounded-xl p-6 shadow-xl text-center">
                <p className="text-white/90 text-sm font-medium mb-2">
                  {t('Il vous manque')}
                </p>
                <p className="text-white text-4xl font-bold tracking-tight">
                  {difference.toFixed(2)} {currency}
                </p>
              </div>

              {/* Message d'encouragement */}
              <p className="text-center text-gray-500 text-sm">
                {t('Ajoutez des articles pour atteindre le minimum de commande')}
              </p>

              {/* Bouton d'action */}
              <button
                {...continueHandler.touchHandlers}
                className={getTouchClasses(`w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-100 ${continueHandler.isPressed ? 'scale-95' : 'hover:scale-[1.02]'}`)}
                style={{ touchAction: 'manipulation' }}
              >
                {t('Continuer mes achats')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
    </Portal>
  );
};

export default MinimumOrderModal;
